import { InformationCircleIcon } from "@heroicons/react/solid";
import { message } from "antd";
import { useMemo, useRef, useState } from "react";
import { HiOutlineMinusCircle, HiOutlinePlusCircle } from "react-icons/hi";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckoutConflictResolution,
  Discount,
  DiscountType,
  Product,
  ProductType,
  ProfitType,
} from "../graphql/types";
import useAddToCart from "../pages/Cart/v2/hooks/useAddToCart";
import ConflictModal from "../pages/Cart/v2/components/ConflictModal";
import { describeGqlError } from "../pages/Cart/v2/utils/errors";
// import { addProductToCart } from "../redux/actions/cartActions";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import { ICartProduct } from "../utils/types";
import { setToggleCartModal } from "../redux/slice/app.slice";
import { addProductToCart, setDigitalProduct } from "../redux/slice/cart.slice";
import _ from "lodash";
import { useShop } from "../hooks/useShop";
import { getDicountedPrice } from "../utils/lib";
import { toast } from "sonner";
import { Info, Minus, Plus } from "lucide-react";

type Props = {};



const AddProductToCartForm = (props: Props) => {
  const dispatch = useAppDispatch();
  const { orderProduct } = useAppSelector((state) => state.orders);
  const { cart } = useAppSelector((state) => state.cart);
  const { user } = useAppSelector((state) => state.auth);
  const [moreProfit, setMoreProfit] = useState(false);
  const [typedSpec, setTypedSpec] = useState(
    orderProduct ? orderProduct.combination_string?.replace("&", "-") : ""
  );
  const [customProfit, setCustomProfit] = useState<string>("");
  const [profit, setProfit] = useState(0);
  const [qty, setQty] = useState(1);
  const { shop, loading: shopLoad } = useShop({
    filter: {
      ownerId: {
        eq: user?.id,
      },
    },
  });

  const navigate = useNavigate();

  // Server-authoritative checkout branch (docs/server-checkout-migration.md P4):
  // when the flag is on, adds go to addCheckoutCartItem (stock & exclusivity
  // enforced server-side) and conflicts surface via ConflictModal. The legacy
  // Redux dispatch below is untouched when the flag is off.
  const {
    serverCheckout,
    addItemToServerCart,
    conflict,
    resolveConflict,
    cancelConflict,
    adding,
  } = useAddToCart();
  const goToCartAfterAdd = useRef(false);

  const finishServerAdd = () => {
    message.success("Product added to cart", 5);
    dispatch(setToggleCartModal(false));
    if (goToCartAfterAdd.current) navigate("/cart");
  };

  const serverAdd = async (goToCart: boolean) => {
    goToCartAfterAdd.current = goToCart;
    try {
      const result = await addItemToServerCart({
        productId: String(orderProduct?.sku),
        qty,
        profit: Number(profit) || 0,
        profitType: ProfitType.Amount,
      });
      if (result === "added") finishServerAdd();
    } catch (e) {
      message.error(describeGqlError(e, "Could not add product to cart"), 5);
    }
  };

  const onResolveConflict = async (resolution: CheckoutConflictResolution) => {
    try {
      if ((await resolveConflict(resolution)) === "added") finishServerAdd();
    } catch (e) {
      message.error(describeGqlError(e, "Could not add product to cart"), 5);
    }
  };

  //hook to suggest profits
  const useSuggestedProfits = (orderProduct: Product | null) => {
    const basePrice = Number(orderProduct?.price || 0);
    return [
      Math.round(basePrice * 0.1),//ten percent
      Math.round(basePrice * 0.15),//fifteen percent
      Math.round(basePrice * 0.20),// twenty percent
    ];
  };

  const suggestedProfit = useSuggestedProfits(orderProduct)

  const selectedProfit = (amount:number) =>{
    setProfit(amount);
    setCustomProfit(amount.toString());
  } 

   const handleCustomProfitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (/^\d*$/.test(value)) {
        setCustomProfit(value);
        setProfit(value ? Number(value) : 0);
      }
    };
      const itemTotal = Number(orderProduct?.price || 0) * qty;
  const totalWithProfit = itemTotal + profit;
  

  const reducePriceBy = useMemo(() => {
    if (orderProduct?.discount) {
      if (orderProduct?.discount?.type === DiscountType.FixedAmount) {
        return Number(orderProduct?.discount.amount);
      }
      if (orderProduct?.discount?.type === DiscountType.Percentage) {
        return (
          (Number(orderProduct?.price) *
            Number(orderProduct.discount.percentage)) /
          100
        );
      }
    }
    return 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderProduct?.discount, orderProduct?.price]);
  const ChangeQty = (e: any) => {
    const inputValue = +e.target.value;
    const aliExpressMOQ = orderProduct?.minimumOrderQuantity || 1;

    if (inputValue < aliExpressMOQ) {
      toast.error(
        "Quantity should be same or more than minimum order quantity"
      );
      return;
    }

    setQty(inputValue);
  };

  const inc = () => {
    if (qty < Number(orderProduct?.qty)) {
      setQty(qty + 1);
    }
  };
  const dec = () => {
    if (qty > 1) {
      setQty(qty - 1);
    }
  };
  const existsInCart = cart?.products.find(
    (prod) => prod.productSKU === orderProduct?.sku
  );

  const addToCart = (e: React.FormEvent) => {
    e.preventDefault();

    if (serverCheckout) {
      void serverAdd(false);
      return;
    }

    if (
      _.isEmpty(cart?.products) ||
      Number(existsInCart?.productQty) + Number(qty) <=
        Number(orderProduct?.qty) ||
      !existsInCart?.productSKU.includes(orderProduct?.sku as string)
    ) {
      const productObj: ICartProduct = {
        imageLink: String(orderProduct?.avatar),
        name: String(orderProduct?.title),
        productPrice: String(orderProduct?.price),
        productQty: qty,
        productSKU: String(orderProduct?.sku),
        profitAdded: profit.toString(),
        spec: String(typedSpec),
        collection: orderProduct?.collection,
        newPrice: (orderProduct?.price as number) - reducePriceBy,
        discountAmount: getDicountedPrice(orderProduct as any) ?? 0,
        supplier_id: orderProduct?.supplier_id,
        availableQty: Number(orderProduct?.qty),
        currency: orderProduct?.currency as string,
        discount: orderProduct?.discount as Discount,
        inHouse: orderProduct?.inhouse as Boolean,
        shippingCost: Number(orderProduct?.shippingCost) || 0,
      };

      dispatch(addProductToCart(productObj));
      message.success("Product added to cart", 5);
      dispatch(setToggleCartModal(false));
    } else {
      message.error("Product quantity limit reached", 5);
    }
  };

  const orderNow = () => {
    if (serverCheckout && orderProduct?.productType === ProductType.Physical) {
      // Digital products stay on the legacy flow (out of scope).
      void serverAdd(true);
      return;
    }
    if (
      _.isEmpty(cart?.products) ||
      Number(existsInCart?.productQty) + Number(qty) <=
        Number(orderProduct?.qty) ||
      !existsInCart?.productSKU.includes(orderProduct?.sku as string)
    ) {
      const productObj: ICartProduct & { productType: any } = {
        imageLink: String(orderProduct?.avatar),
        name: String(orderProduct?.title),
        productPrice: String(orderProduct?.price),
        productQty: qty,
        productSKU: String(orderProduct?.sku),
        profitAdded: profit.toString(),
        spec: String(typedSpec),
        supplierPrice: orderProduct?.cost_price,
        collection: orderProduct?.collection,
        newPrice: (orderProduct?.price as number) - reducePriceBy,
        discountAmount: getDicountedPrice(orderProduct as any) ?? 0,
        supplier_id: orderProduct?.supplier_id,
        currency: orderProduct?.currency as string,
        productType: orderProduct?.productType,
        discount: orderProduct?.discount as Discount,
        inHouse: orderProduct?.inhouse as Boolean,
        shippingCost: Number(orderProduct?.shippingCost),
      };
      if (productObj.productType === ProductType.Physical) {
        dispatch(addProductToCart(productObj));
        message.success("Product added to cart", 5);
        dispatch(setToggleCartModal(false));
        navigate("/cart");
      } else {
        navigate("/cart/digital/summary");
        dispatch(setToggleCartModal(false));
        dispatch(setDigitalProduct(productObj));
      }
    } else {
      message.error("Product quantity limit reached", 5);
    }
  };

  // console.log("dp", getDicountedPrice(orderProduct as any));

  // function clearCartAndAdd(e: React.FormEvent) {
  //   dispatch(clearCart());
  //   addToCart(e);
  //   dispatch(setToggleCartModal(false));
  // }

  // const supplierIds = cart?.products.map((item) => item.supplier_id);

  // if (
  //   cart?.products.length >= 1 &&
  //   !supplierIds.includes(orderProduct?.supplier_id || null)
  // )
  //   return (
  //     <div className="flex">
  //       <div className="w-full flex flex-col flex-1 overflow-y-scroll">
  //         <div className="flex flex-col w-full">
  //           <div className="flex-1 p-2 bg-gray-100 my-2 rounded-md">
  //             <div className="flex items-start">
  //               <div className="flex-shrink-0 pt-0.5">
  //                 <img
  //                   className="h-16 w-16 rounded-md"
  //                   src={orderProduct?.avatar}
  //                   alt=""
  //                 />
  //               </div>
  //               <div className="ml-3 w-0 flex-1 flex flex-col">
  //                 <span className="text-xs font-medium text-gray-500">
  //                   {orderProduct?.title}
  //                 </span>
  //                 <span className="mt-1 text-sm font-bold text-gray-900">
  //                   {orderProduct?.currency} {orderProduct?.price}
  //                 </span>
  //                 <span className="mt-1 text-sm font-medium text-gray-500">
  //                   {orderProduct?.sku}
  //                 </span>
  //               </div>
  //             </div>
  //           </div>
  //           <span className={`text-xs font-medium text-yellow-500 mt-1`}>
  //             You are limited to adding items from a specific supplier one at a
  //             time to your cart.
  //           </span>
  //         </div>
  //         <div className="flex space-x-3 my-2">
  //           <div className="flex w-full">
  //             <button
  //               type="button"
  //               className={`w-full flex justify-center py-4 px-4 text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 border-2 border-blue-500 text-blue-500 hover:bg-blue-600 hover:text-white`}
  //               onClick={clearCartAndAdd}
  //             >
  //               Clear cart and add product
  //             </button>
  //           </div>
  //           <div className="flex w-full">
  //             <button
  //               type="button"
  //               className={`w-full flex justify-center py-4 px-4 border border-transparent text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 ${"bg-blue-500 text-white"}`}
  //               onClick={orderNow}
  //             >
  //               Cancel
  //             </button>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   );

  if (shopLoad) return null;

  if (!shop && !shopLoad)
    return (
      <div>
        <div>
          <h2 className="text-lg">Activate your shop</h2>
          <span className="text-gray-700">
            To add profit to a product, you need to activate your shop.
          </span>
        </div>
        <Link
          to={"/shop"}
          onClick={() => dispatch(setToggleCartModal(false))}
          className={`w-full flex justify-center items-center py-3 px-4 border border-transparent text-base font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 bg-blue-500 text-white hover:bg-blue-600 hover:text-white mt-5`}
        >
          Activate shop
        </Link>
      </div>
    );

  return (
       <>
      <ConflictModal
        conflict={conflict}
        onResolve={onResolveConflict}
        onCancel={cancelConflict}
        busy={adding}
      />
      <div className="w-full">
        <div className="flex items-start space-x-2 bg-blue-50 text-blue-700 text-xs p-3">
          <Info size={16} className="mt-0.5" />
          <p>
            You can only add product to cart if you are ordering multiple
            items for the same customer. Else checkout as a single order.
          </p>
        </div>

        {/* Product Section */}
        <div className="p-4 flex space-x-4 border-b">
          <img
            src={orderProduct?.avatar}
            alt="Product"
            className="w-28 h-28 object-cover rounded-lg"
          />
          <div className="flex-1">
            <h3 className="text-gray-900 font-semibold">
              {orderProduct?.title}
            </h3>
            {orderProduct?.productType === "Digital" && (
              <p className="text-gray-500 text-sm">Type: Full Copy</p>
            )}
            <p className="text-gray-900 font-bold mt-2">
              {orderProduct?.currency} {orderProduct?.price}
            </p>

            {/* Quantity controls */}
            {orderProduct?.productType === ProductType.Physical && (
              <div className="flex items-center space-x-3 mt-2">
                <button
                  type="button"
                  title="Decrease"
                  onClick={dec}
                  disabled={qty <= 1}
                  className={`w-7 h-7 flex items-center justify-center border rounded-full transition-colors ${
                    qty <= 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Minus className="h-4 w-4" />
                </button>

                <input
                  type="number"
                  id="productQty"
                  name="productQty"
                  value={qty}
                  onChange={ChangeQty}
                  max={
                    Number(orderProduct?.qty) ||
                    Number(orderProduct?.minimumOrderQuantity)
                  }
                  className="w-16 text-center text-gray-900 font-medium border-gray-300 rounded-md focus:ring-tendo-active focus:border-tendo-active sm:text-sm appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                />

                <button
                  type="button"
                  title="Increase"
                  onClick={inc}
                  className="w-7 h-7 flex items-center justify-center border rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Profit Section */}
        <div className="p-4 space-y-3 border-b">
          <div>
            <h4 className="text-gray-900 font-medium">Add your profit</h4>
            <p className="text-sm text-gray-600">
              You've added {orderProduct?.currency}{" "}
              {parseFloat(profit.toString()).toFixed(2)} as a profit to this
              product
            </p>
          </div>

          {/* Preset profit options - FIXED */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Suggested Profit
            </label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {suggestedProfit.map((profits, index) => (
                <button
                  key={index}
                  type="button"
                  title="profit buttons"
                  className={`py-2 px-4 text-sm font-medium rounded-md ${
                    profit === profits
                      ? "bg-buttonPrimary text-white"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                  onClick={() => selectedProfit(profits)}
                >
                  {orderProduct?.currency} {profits}
                </button>
              ))}
            </div>
          </div>

          {/* Custom profit input  */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Profit
            </label>
            <div className="mt-1 relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 sm:text-sm">
                  {orderProduct?.currency}
                </span>
              </div>
              <input
                type="text"
                name="custom-profit"
                id="custom-profit"
                value={customProfit}
                onChange={handleCustomProfitChange}
                className="focus:ring-buttonPrimary focus:border-buttonPrimary block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                placeholder="0"
              />
            </div>
            {moreProfit && (
              <span className="text-xs font-medium text-yellow-500 mt-1 block">
                We advice you add profit up to 100% of product price.
              </span>
            )}
          </div>

          {orderProduct?.combination_string && (
            <div className="mb-3">
              <label htmlFor="spec" className="block text-sm font-medium">
                Product spec
              </label>
              <div className="mt-2 relative rounded-md shadow-sm">
                <input
                  type="text"
                  id="spec"
                  autoComplete="spec"
                  className="focus:ring-sokoBlue focus:border-sokoBlue block w-full pl-7 pr-12 py-2 sm:text-sm border-gray-300 border-2 rounded-md placeholder:text-gray-200 disabled:bg-gray-200"
                  placeholder="Black small"
                  value={typedSpec}
                  onChange={(e) => {
                    setTypedSpec(e.target.value);
                  }}
                  disabled
                />
              </div>
            </div>
          )}
        </div>

        {/* Summary - FIXED */}
        <div className="p-4 text-sm text-gray-700 space-y-1 border-b">
          <div className="flex justify-between">
            <span>Item ({qty}x)</span>
            <span>
              {orderProduct?.currency} {itemTotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>My Profit</span>
            <span>
              {orderProduct?.currency} {profit.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between font-semibold text-gray-900 border-t pt-2">
            <span>Total</span>
            <span>
              {orderProduct?.currency} {totalWithProfit.toFixed(2)}
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-4 space-y-2">
          {orderProduct?.productType === ProductType.Physical && (
            <button
              type="button"
              onClick={addToCart}
              className="w-full border-2 border-[#0B3768] text-[#0B3768] py-2.5 rounded-lg font-medium hover:bg-[#0B3768] hover:text-white transition-colors"
            >
              Add to Cart
            </button>
          )}
          <button
            type="button"
            onClick={orderNow}
            className="w-full bg-[#0B3768] text-white py-2.5 rounded-lg font-medium hover:bg-[#0d447f] transition-colors"
          >
            Order Now
          </button>
        </div>
      </div>
      </>
   );
};

export default AddProductToCartForm;
